/**
 * liveTimingClient — fetches race classification from livetiming.formula1.com
 * static data files (or a configured proxy/mirror).
 * -------------------------------------------------------------------------
 * Coverage (official endpoint):
 *   Index.json (year calendar):      public for 2018-2021, 2023-2026.
 *                                    2022 and 2012-2017 blocked (403).
 *                                    2024 Index.json only from Spanish GP.
 *   Session feeds (TimingData.json,
 *   DriverList.json, etc.):          2018-2021, 2023-2026.
 *   SessionData.json:                 2020-2021, 2023-2026 (blocked 2018-2019).
 *   PitStopSeries.json / OvertakeSeries: late 2024 onward.
 *   RcmSeries.json:                   always 403 — use RaceControlMessages.json.
 *   Telemetry (CarData.z, Position.z): 200 + raw zlib inflate.
 *
 * Source strategy:
 *   NO AUTHENTICATION IS REQUIRED (verified Session 7). Every feed returns 200
 *   unauthenticated. The only blocker is CORS — livetiming sends no
 *   Access-Control-Allow-Origin header — so the request must go through the
 *   Vite dev proxy at /api/livetiming/* (or an equivalent edge proxy in prod).
 *   LIVETIMING_AUTH_TOKEN is optional and normally unnecessary; earlier
 *   sessions believed a token was needed because malformed paths answer 403.
 *
 *   Session resolution (meeting_key + session_key → session path) is done via
 *   Index.json. See resolveSessionPath for the path-shape trap.
 *
 * IMPORTANT — this tier is PROVISIONAL, not official:
 *   TimingData is the on-track order at the chequered flag. Post-race stewards'
 *   penalties are NOT applied. At the 2026 Monaco GP, Gasly crossed the line
 *   P7 but was classified P3, and Pérez crossed P10 and dropped to P15.
 *   Livetiming has no post-penalty classification feed (Results.json,
 *   Classification.json, SessionResults.json and FinalClassification.json all
 *   404-as-403), and TopThree.json is on-track order too. Penalties appear only
 *   as free text in RaceControlMessages. Callers surface this via `_provisional`.
 *
 * Feed architecture (three feeds merged at runtime):
 *   TimingData.json   — position, gaps, laps, retired, best-lap times, sectors
 *   DriverList.json   — driver name, TLA, team name, team colour, headshot
 *   TimingAppData.json — grid position, stint/tyre data
 *   SessionInfo.json  — meeting name, circuit, country, start/end date
 *   WeatherData.json  — air temp, track temp, humidity, wind, rainfall
 *   RaceControlMessages — flags, incidents, penalties timeline
 */
import { FL_BONUS_YEARS, POINTS_TABLE } from './pointsConfig.js';

const PROXY_BASE = '/api/livetiming';
const CACHE = new Map();
const TTL = 300_000;

/* ---------------------------------------------------------------------------
 * Low-level fetcher
 * ------------------------------------------------------------------------- */

async function get(path, { signal } = {}) {
  const cached = CACHE.get(path);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  let res;
  try {
    res = await fetch(`${PROXY_BASE}${path}`, { signal, headers: { Accept: 'application/json' } });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new Error(`Livetiming fetch failed: ${err.message}`, { cause: err });
  }
  if (res.status === 403) {
    throw new Error('Livetiming 403 — auth required or year not available');
  }
  if (!res.ok) {
    throw new Error(`Livetiming ${res.status} at ${path}`);
  }
  const value = await res.json();
  CACHE.set(path, { value, expiresAt: Date.now() + TTL });
  return value;
}

/* ---------------------------------------------------------------------------
 * Feed helpers
 * ------------------------------------------------------------------------- */

/**
 * fetchFeed — every session feed lives at `/static/{sessionPath}{Name}.json`,
 * where sessionPath is the ready-made value from Index.json (see
 * resolveSessionPath). Do NOT re-prepend the year — it is already in there.
 */
async function fetchFeed(sessionPath, name, { signal } = {}) {
  return get(`/static/${sessionPath}${name}.json`, { signal });
}

/**
 * resolveSessionPath — maps an OpenF1 meeting_key + session_key onto the
 * livetiming static path for that session.
 *
 * The key matching is sound: OpenF1 derives its meeting_key/session_key
 * straight from livetiming, so they are identical values (verified 24/24 for
 * 2025). What was broken was the path building.
 *
 * `session.Path` from Index.json is ALREADY a complete, year-prefixed,
 * trailing-slashed path:
 *
 *   "2026/2026-07-19_Belgian_Grand_Prix/2026-07-19_Race/"
 *
 * The previous implementation split that into meetingPath + sessionPath and
 * the caller re-prepended the year, producing
 *
 *   /static/2026/2026/2026-07-19_Belgian_Grand_Prix/2026-07-19_Race//TimingData.json
 *
 * (doubled year, and an empty final segment because of the trailing slash).
 * S3 answers a malformed key with **403, not 404**, which is why earlier
 * sessions concluded the endpoint needed an F1TV token. It does not — the
 * whole Tier 2 fallback had simply never worked.
 *
 * @returns {Promise<string|null>} path with trailing slash, or null
 */
export async function resolveSessionPath(year, meetingKey, sessionKey, { signal } = {}) {
  const index = await get(`/static/${year}/Index.json`, { signal });
  if (!index?.Meetings) return null;

  const normalise = (p) => (p ? String(p).replace(/^\/+/, '').replace(/\/*$/, '/') : null);

  // Preferred: exact meeting + session key match.
  for (const meeting of index.Meetings) {
    if (meeting.Key != meetingKey) continue;
    for (const session of meeting.Sessions ?? []) {
      if (session.Key == sessionKey && session.Path) return normalise(session.Path);
    }
  }

  // Fallback: session key alone, any meeting in the year.
  for (const meeting of index.Meetings) {
    for (const session of meeting.Sessions ?? []) {
      if (session.Key == sessionKey && session.Path) return normalise(session.Path);
    }
  }
  return null;
}

/* ---------------------------------------------------------------------------
 * Public API
 * ------------------------------------------------------------------------- */

/**
 * fetchRaceResult — rich race classification from livetiming static data.
 * Merges TimingData.json, DriverList.json, TimingAppData.json, SessionInfo.json,
 * and WeatherData.json into a single normalised output shape.
 *
 * @param {number} year
 * @param {number} sessionKey  — OpenF1 session_key (used for path resolution)
 * @param {number} meetingKey  — OpenF1 meeting_key
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object|null>}
 */
export async function fetchRaceResult(year, sessionKey, meetingKey, { signal } = {}) {
  if (!sessionKey || !meetingKey) return null;

  const sessionPath = await resolveSessionPath(year, meetingKey, sessionKey, { signal });
  if (!sessionPath) return null;

  // Fetch the core feeds in parallel
  const [timingData, driverList, appData, sessionInfo, weatherFeed] = await Promise.all([
    fetchFeed(sessionPath, 'TimingData', { signal }).catch(() => null),
    fetchFeed(sessionPath, 'DriverList', { signal }).catch(() => null),
    fetchFeed(sessionPath, 'TimingAppData', { signal }).catch(() => null),
    fetchFeed(sessionPath, 'SessionInfo', { signal }).catch(() => null),
    fetchFeed(sessionPath, 'WeatherData', { signal }).catch(() => null),
  ]);

  if (!timingData?.Lines) return null;
  if (!driverList) return null;

  const entries = Object.values(timingData.Lines)
    .filter((l) => l.Position > 0)
    .sort((a, b) => a.Position - b.Position);

  if (entries.length === 0) return null;

  const winnerLaps = entries[0].NumberOfLaps || 0;

  // Build driver lookup from DriverList.json (flat keyed by RacingNumber)
  const driverMeta = {};
  if (driverList) {
    for (const [num, d] of Object.entries(driverList)) {
      driverMeta[num] = d;
    }
  }

  // Build grid lookup from TimingAppData.json
  const gridPositions = {};
  if (appData?.Lines) {
    for (const [num, entry] of Object.entries(appData.Lines)) {
      if (entry.GridPos) gridPositions[num] = parseInt(entry.GridPos, 10);
    }
  }

  // Find fastest lap driver number
  let fastestLapDriverNum = null;
  let fastestLapTime = Infinity;
  for (const entry of entries) {
    const bl = entry.BestLapTime?.Value;
    if (!bl) continue;
    const secs = parseLapTime(bl);
    if (secs !== null && secs < fastestLapTime) {
      fastestLapTime = secs;
      fastestLapDriverNum = entry.RacingNumber;
    }
  }

  const results = entries.map((entry) => {
    const num = entry.RacingNumber;
    const meta = driverMeta[num] ?? {};
    const status = mapTimingStatus(entry, winnerLaps);
    const grid = gridPositions[num] ?? '-';
    const position = parseInt(entry.Position, 10);
    const basePts = pointsForPosition(entry.Position, status);
    const isFL = fastestLapDriverNum !== null && num === fastestLapDriverNum;
    // The FL bonus point (2019–2024) only counts if the driver is classified in
    // the top 10 — see pointsConfig.js. Without this guard a P16 fastest lap
    // scored a phantom point (e.g. 2021 Britain, 2022 Japan).
    const flEligible = isFL
      && !!FL_BONUS_YEARS[year]
      && Number.isFinite(position)
      && position <= 10
      && status !== 'DNF' && status !== 'DNS' && status !== 'DSQ';

    return {
      position,
      driver_number: parseInt(num, 10),
      driver_name: meta.FullName ?? `Driver #${num}`,
      team_name: meta.TeamName ?? 'Unknown',
      time_gap: formatTimingGap(entry, winnerLaps, status),
      points: basePts + (flEligible ? 1 : 0),
      status,
      laps: entry.NumberOfLaps ?? '-',
      grid,
      hasFastestLap: isFL,
      season: year,
      flBonusActive: !!FL_BONUS_YEARS[year],
    };
  });

  // Fastest lap object
  let fastestLap = { driver_name: '-', driver_number: 0, lap_time: '-', lap_number: '-' };
  if (fastestLapDriverNum) {
    const flMeta = driverMeta[fastestLapDriverNum] ?? {};
    const flEntry = entries.find((e) => e.RacingNumber === fastestLapDriverNum);
    fastestLap = {
      driver_name: flMeta.FullName ?? `#${fastestLapDriverNum}`,
      driver_number: parseInt(fastestLapDriverNum, 10),
      lap_time: flEntry?.BestLapTime?.Value ?? '-',
      lap_number: flEntry?.BestLapTime?.Lap ?? '-',
    };
  }

  // Weather
  const weather = weatherFeed
    ? {
        air_temp_c: weatherFeed.AirTemp ?? '-',
        track_temp_c: weatherFeed.TrackTemp ?? '-',
        humidity_pct: weatherFeed.Humidity ?? '-',
        conditions: parseFloat(weatherFeed.Rainfall) > 0 ? 'Rain' : 'Dry',
        estimated: false,
      }
    : { air_temp_c: '-', track_temp_c: '-', humidity_pct: '-', conditions: '-', estimated: false };

  // Meeting info
  const meeting = sessionInfo?.Meeting ?? {};
  const circuitShort = meeting.Circuit?.ShortName ?? '';

  return {
    meeting_key: meetingKey,
    meeting_name: meeting.Name ?? `${circuitShort} Grand Prix`,
    meeting_official_name: meeting.OfficialName ?? '',
    location: meeting.Location ?? '',
    country_name: meeting.Country?.Name ?? '',
    date_start: sessionInfo?.StartDate ?? '',
    session_key: sessionKey,
    session_name: sessionInfo?.Name ?? 'Race',
    session_type: sessionInfo?.Type ?? 'Race',
    lap_count: winnerLaps || '-',
    weather,
    fastest_lap: fastestLap,
    pole_position: { driver_name: '-', driver_number: 0, lap_time: '-' },
    results,
    _source: 'livetiming',
    // On-track order at the flag — post-race penalties are not reflected.
    // See the module header. The UI labels this so users aren't misled.
    _provisional: true,
  };
}

/* ---------------------------------------------------------------------------
 * Helpers — status / gap / points
 * ------------------------------------------------------------------------- */

/**
 * mapTimingStatus — bucket a TimingData line into the five UI statuses.
 *
 * `entry.Status` is USELESS for separating Finished from Lapped: a survey of
 * 16 races found 1088 on every classified runner regardless of lap deficit,
 * and every non-1088 code also carried Retired/Stopped. So the lap count is
 * the only signal for "Lapped", and 87 rows in that sample were previously
 * mislabelled "Finished" (losing the yellow status colour in the UI).
 *
 * DSQ is not representable — livetiming has no disqualification marker, and
 * post-race exclusions never reach the static feed at all. Tier 2 therefore
 * cannot report DSQ; Jolpica (primary) can.
 */
function mapTimingStatus(entry, winnerLaps) {
  if (entry.Retired || entry.Stopped) return 'DNF';
  const laps = entry.NumberOfLaps ?? 0;
  if (laps === 0) return 'DNS';
  if (Number.isFinite(winnerLaps) && winnerLaps > 0 && laps < winnerLaps) return 'Lapped';
  return 'Finished';
}

/**
 * formatTimingGap — produce the same gap vocabulary the Jolpica and OpenF1
 * tiers use, so the UI cannot tell which tier served the row.
 *
 * Livetiming's own formats differ from the other tiers in two ways:
 *   - lap-down runners are "1L" / "16L", not "+1 Lap"
 *   - gaps past a minute stay in raw seconds ("+73.753", not "+1:13.753")
 *
 * P1 has no total race time anywhere in the static feeds (TimingData is a
 * final snapshot, not a lap log), so the leader row shows "Winner" rather
 * than the previous behaviour of printing their *best lap*, which read like
 * a race time and was simply wrong.
 */
function formatTimingGap(entry, winnerLaps, status) {
  if (parseInt(entry.Position, 10) === 1) return 'Winner';
  if (status === 'DNF' || status === 'DNS') return status;

  if (status === 'Lapped') {
    const def = winnerLaps - (entry.NumberOfLaps || 0);
    return def > 0 ? `+${def} Lap${def > 1 ? 's' : ''}` : '+1 Lap';
  }

  const gap = entry.GapToLeader;
  if (gap && gap !== '') return normaliseGap(gap);
  return 'Finished';
}

/** normaliseGap — "12L" → "+12 Laps", "+73.753" → "+1:13.753". */
function normaliseGap(raw) {
  const s = String(raw).trim();

  const lapForm = s.match(/^\+?(\d+)\s*L(?:AP)?S?$/i);
  if (lapForm) {
    const n = parseInt(lapForm[1], 10);
    return `+${n} Lap${n > 1 ? 's' : ''}`;
  }

  const secForm = s.match(/^\+?(\d+(?:\.\d+)?)$/);
  if (secForm) {
    const total = parseFloat(secForm[1]);
    if (total >= 60) {
      const mins = Math.floor(total / 60);
      return `+${mins}:${(total - mins * 60).toFixed(3).padStart(6, '0')}`;
    }
    return `+${total.toFixed(3)}`;
  }

  return s;
}

function parseLapTime(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split(':');
  if (parts.length === 2) {
    const min = parseInt(parts[0], 10);
    const sec = parseFloat(parts[1]);
    if (!isNaN(min) && !isNaN(sec)) return min * 60 + sec;
  }
  return null;
}

function pointsForPosition(posStr, status) {
  if (status === 'DNF' || status === 'DNS' || status === 'DSQ') return 0;
  const pos = parseInt(posStr, 10);
  if (isNaN(pos) || pos < 1) return 0;
  return POINTS_TABLE[pos - 1] ?? 0;
}
